import React from 'react';
import useRegisterMemberWorkflow from './useRegisterMemberWorkflow';
import RegisterMemberForm from './RegisterMemberForm';
import CollectPaymentModal from './CollectPaymentModal';
import './Member.css';
import '../lib-page-header.css';

const FORM_ID = 'register-member-page-form';

// Backward-compatible full-page route (/librarian/members/register). Normal
// registration now happens through RegisterMemberModal from the Members
// page — this route reuses the exact same hook/form so the two never drift.
const RegisterMember = () => {
  const wf = useRegisterMemberWorkflow();

  const depositRequired = Number(wf.result?.deposit_required_amount || 0);
  const canShowPayment = wf.result && depositRequired > 0 && wf.paymentStatus !== 'success' && wf.canCollectPayment;

  return (
    <div className="container-fluid mt-3 register-member-page">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-user-plus" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Register Member</h3>
            <div className="lib-page-header__subtitle">
              Register a school parent / outsider and collect their membership payment
            </div>
          </div>
        </div>
      </div>

      {!wf.result ? (
        <div className="card shadow-sm mt-3">
          <div className="card-body">
            <RegisterMemberForm
              formId={FORM_ID}
              form={wf.form}
              onChange={wf.handleChange}
              onMemberSourceChange={wf.handleMemberSourceChange}
              isSchoolParent={wf.isSchoolParent}
              onSubmit={wf.handleRegister}
              profileImageFile={wf.profileImageFile}
              onProfileImageChange={wf.handleProfileImageChange}
              outsiderDepositAmount={wf.outsiderDepositAmount}
            />

            <div className="mt-3 d-flex justify-content-end">
              <button
                type="submit"
                form={FORM_ID}
                className="btn btn-primary"
                disabled={wf.saving || !wf.form.member_source}
              >
                {wf.saving ? 'Registering...' : 'Register Member'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="card shadow-sm mt-3">
          <div className="card-body">
            <h5 className="mb-3">Registration Successful</h5>

            <div className="row mb-2">
              <div className="col-md-4"><strong>Member:</strong> {wf.form.first_name} {wf.form.last_name}</div>
              <div className="col-md-4"><strong>Mobile:</strong> {wf.form.phone}</div>
              <div className="col-md-4"><strong>Membership No:</strong> {wf.result.membership_no}</div>
            </div>
            <div className="row mb-2">
              <div className="col-md-4"><strong>Member Type:</strong> {String(wf.result.member_source || '').replace('_', ' ')}</div>
              <div className="col-md-4"><strong>Membership Status:</strong> {wf.result.membership_status}</div>
              <div className="col-md-4">
                <strong>Required Deposit:</strong> ₹{depositRequired}
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-4">
                <strong>Payment Status:</strong>{' '}
                <span className={`badge ${wf.paymentStatus === 'success' ? 'bg-success' : 'bg-warning text-dark'}`}>
                  {wf.paymentStatus}
                </span>
              </div>
            </div>

            {canShowPayment && (
              <div className="d-flex gap-2">
                {!wf.lastOrderId ? (
                  <button type="button" className="btn btn-primary" onClick={() => wf.setShowPaymentModal(true)}>
                    Collect Payment
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={wf.handleCheckStatus}
                      disabled={wf.checkingStatus}
                    >
                      {wf.checkingStatus ? 'Checking...' : 'Check Payment Status'}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => wf.setShowPaymentModal(true)}>
                      Retry Payment
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="mt-3 d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={wf.resetAll}>
                Register Another Member
              </button>
              <a href="/librarian/members" className="btn btn-outline-primary">
                View Members List
              </a>
            </div>
          </div>
        </div>
      )}

      {wf.showPaymentModal && wf.result && (
        <CollectPaymentModal
          memberId={wf.result.member_id}
          membershipId={wf.result.membership_id}
          amount={wf.result.deposit_required_amount}
          memberName={`${wf.form.first_name} ${wf.form.last_name}`.trim()}
          membershipNo={wf.result.membership_no}
          purpose="Membership Security Deposit"
          onClose={() => wf.setShowPaymentModal(false)}
          onSuccess={() => {
            wf.setPaymentStatus('success');
            wf.setShowPaymentModal(false);
          }}
          onOrderCreated={(orderId) => wf.setLastOrderId(orderId)}
        />
      )}
    </div>
  );
};

export default RegisterMember;
